import { Check, X } from "lucide-react";
import { PlanBanner } from "./plan-banner";
import { UsageStats } from "./usage-stats";
import { Plans } from "./plans";
import { cn } from "@/lib/utils";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import { show } from "@intercom/messenger-js-sdk";
import { useSubscriptionInfo } from "@/hooks/pricing/useSubscriptionInfo";

interface PricingProps {
  onClose?: () => void;
}

export default function Pricing({ onClose }: PricingProps) {
  const { usingPrompts } = useSubscriptionInfo();

  return (
    <div className="w-full min-h-screen bg-[#131519] min-w-screen flex items-center justify-center py-4">
      <div className="max-w-[1154px] mx-auto">
        <div className="flex items-start justify-between mb-6">
          <h1 className="text-[28px] leading-[34px] font-medium text-[#F5F5F6] tracking-[-0.03em]">
            My Subscription
          </h1>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[#2F3035] rounded-md transition-colors"
          >
            <X className="w-5 h-5 text-[#F5F5F6]" />
          </button>
        </div>

        <div className="space-y-6">
          {!usingPrompts && <PlanBanner />}
          <div className="grid grid-cols-2 gap-[30px]">
            <div className="space-y-8">
              <UsageStats />
              <div className="space-y-6">
                <div className="rounded-xl bg-gradient-to-b from-[#1F1F22] to-[#131519]]">
                  <div className="flex flex-row">
                    <div className="p-[30px]">
                      <h3 className="text-base text-[#F5F5F6] mb-6">
                        {usingPrompts
                          ? "All plans include:"
                          : "What's included:"}
                      </h3>
                      <div className="flex flex-col gap-6">
                        <div className="text-[#9F9FA4] text-base leading-[22px] flex flex-row gap-2 items-center">
                          <div
                            className={cn(
                              "flex justify-center mr-2",
                              !usingPrompts && "hidden"
                            )}
                          >
                            <div className="w-6 h-6 rounded bg-[#2F3035] flex items-center justify-center">
                              <Check className="w-4 h-4 text-[#F5F5F6]" />
                            </div>
                          </div>
                          <span>Full access to code & reasoning agents</span>
                        </div>
                        <div className="text-[#9F9FA4] text-base leading-[22px] flex flex-row gap-2 items-center">
                          <div
                            className={cn(
                              "flex justify-center mr-2",
                              !usingPrompts && "hidden"
                            )}
                          >
                            <div className="w-6 h-6 rounded bg-[#2F3035] flex items-center justify-center">
                              <Check className="w-4 h-4 text-[#F5F5F6]" />
                            </div>
                          </div>
                          <span>Add 250 bonus prompts for $50 anytime</span>
                        </div>
                        <div className="text-[#9F9FA4] text-base leading-[22px] flex flex-row gap-2 items-center  ">
                          <div
                            className={cn(
                              "flex justify-center mr-2",
                              !usingPrompts && "hidden"
                            )}
                          >
                            <div className="w-6 h-6 rounded bg-[#2F3035] flex items-center justify-center">
                              <Check className="w-4 h-4 text-[#F5F5F6]" />
                            </div>
                          </div>
                          <span>
                            Error fixes are free and do not count towards your
                            monthly prompts
                          </span>
                        </div>
                      </div>
                    </div>
                    <div
                      className={cn(
                        "text-center border-x border-[#2F3035] py-[30px] min-w-32",
                        usingPrompts && "hidden"
                      )}
                    >
                      <h3 className="text-base text-[#F5F5F6] mb-6">
                        Your plan
                      </h3>
                      <div className="flex flex-col gap-6">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="flex justify-center mb-6">
                            <div className="w-6 h-6 rounded bg-[#2F3035] flex items-center justify-center">
                              <X className="w-4 h-4 text-[#F5F5F6]" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    <div
                      className={cn(
                        "text-center py-[30px] min-w-32",
                        usingPrompts && "hidden"
                      )}
                    >
                      <h3 className="text-base text-[#F5F5F6] mb-6">Upgrade</h3>
                      <div className="flex flex-col gap-6">
                        {[1, 2, 3].map((i) => (
                          <div key={i} className="flex justify-center mb-6">
                            <div className="w-6 h-6 rounded bg-[#6F55F2] flex items-center justify-center">
                              <Check className="w-4 h-4 text-[#F5F5F6]" />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-gray-400">
                    Looking for <span className="text-white">Enterprise</span>{" "}
                    plans?
                  </p>
                  <a
                    href="https://tempo.new/ai-agent-plus"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-n-500 hover:text-primary-n-400"
                  >
                    Check out Agent+ pricing
                  </a>
                </div>
                <div className="mt-6 text-center">
                  <p className="text-gray-400">
                    Any questions?{" "}
                    <span
                      className="text-primary-n-500 hover:text-primary-n-400 cursor-pointer"
                      onClick={() => {
                        show();
                      }}
                    >
                      Send us a message
                    </span>
                  </p>
                </div>
              </div>
            </div>
            <div>
              <Plans />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
